import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

const ProtectedRoute = ({ allowedRoles = [] }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only check localStorage once on mount to prevent re-renders
    if (hasCheckedRef.current) return;
    
    logger.debug('[ProtectedRoute] Checking user authentication...', { pathname: location.pathname });
    
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // Only set user if it has a valid token
        if (parsedUser && parsedUser.token) {
          logger.debug('[ProtectedRoute] Authenticated user found', { role: parsedUser.role });
          setUser(parsedUser);
        } else {
          // Invalid user data (no token) - treat as anonymous
          logger.debug('[ProtectedRoute] User data found but no token - treating as anonymous');
          setUser(null);
        }
      } catch (error) {
        Sentry.captureException(error);
        logger.error('[ProtectedRoute] Error parsing user data', { error: error.message });
        setUser(null);
      }
    } else {
      // No user data - anonymous user
      logger.debug('[ProtectedRoute] No user data - anonymous user');
      setUser(null);
    }
    setIsLoading(false);
    hasCheckedRef.current = true;
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-fg-60">
        Loading...
      </div>
    );
  }

  // 1. USER NOT LOGGED IN → Allow anonymous access to chat, redirect others to login
  if (!user?.token) {
    // Allow anonymous access to chat routes
    const isChatRoute = location.pathname.startsWith('/chat');
    logger.debug('[ProtectedRoute] Anonymous user check', { pathname: location.pathname, isChatRoute });
    if (isChatRoute) {
      // Allow anonymous users to access chat
      logger.debug('[ProtectedRoute] Allowing anonymous access to chat');
      return <Outlet />;
    }
    // For other routes, redirect to login
    logger.debug('[ProtectedRoute] Redirecting anonymous user to login (not a chat route)');
    return <Navigate to="/login" replace />;
  }

  // 1.5. EMAIL VERIFICATION CHECK - Block access if email not verified (skip for admin/super admin)
  const emailVerified = user?.emailVerified;
  const userRole = user?.role;
  const isAdmin = userRole === "admin" || userRole === "super admin" || userRole === "super_admin";
  
  logger.debug("ProtectedRoute - Email verification check", { emailVerified, userRole, isAdmin });
  
  // Only block if emailVerified is explicitly false AND user is not an admin
  // If undefined (legacy support), allow access (assume verified)
  if (!isAdmin && emailVerified === false) {
    // Clear invalid user data and redirect to login
    localStorage.removeItem("user");
    logger.warn("ProtectedRoute - Email not verified, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // 1.6. 🎯 BILLING BYPASS CHECK - Sponsored/Manual Users
  // If billing is not required (sponsored accounts), skip all subscription/trial checks
  if (user.isBillingRequired === false) {
    logger.debug('✅ Billing bypass - Allowing access for sponsored user');
    // Allow access to all routes based on role check only
    // Skip trial and subscription checks below
    
    // 3. ROLE CHECK (moved up for sponsored users)
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      if (user.role === "employee") {
        return <Navigate to="/chat" replace />;
      } else if (user.role === "admin" || user.role === "owner") {
        return <Navigate to="/org/dashboard" replace />;
      } else {
        return <Navigate to="/" replace />;
      }
    }
    
    return <Outlet />;
  }

  // 2. INDIVIDUAL USER TRIAL CHECK - Only check for pending trial activation
  if (user.role === "individual") {
    const trial = user?.trial;
    const status = trial?.status;

    // Trial pending → redirect to /activate-trial
    // Only redirect if not already on the activate-trial page to prevent loops
    if (status === "pending" && location.pathname !== "/activate-trial") {
      return <Navigate to="/activate-trial" replace />;
    }

    // Note: Trial expiration check is handled in ChatSidebar via API call
    // to ensure we always use fresh server data, not stale localStorage
  }

  // 3. ROLE CHECK
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    if (user.role === "employee") {
      return <Navigate to="/chat" replace />;
    } else if (user.role === "admin" || user.role === "owner") {
      return <Navigate to="/org/dashboard" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
